<template>
  <div class="mp-user-list">
    <el-alert
      v-if="showToken"
      type="warning"
      :closable="false"
      class="token-alert"
      title="用户管理接口已改为仅管理员可访问"
    >
      <div class="token-inline">
        <span>请在 EMS 小程序「用户管理」页复制你的管理员令牌并粘贴：</span>
        <el-input v-model="tokenInput" placeholder="粘贴 Token" style="width: 300px; margin: 0 8px" />
        <el-button type="primary" size="small" @click="saveToken">保存并刷新</el-button>
      </div>
    </el-alert>
    <!-- 查询条件：折叠后只显示标题栏 -->
    <el-card class="search-card" shadow="never">
      <div slot="header" class="search-header">
        <span class="search-title" @click="searchExpanded = !searchExpanded">
          查询条件
          <i :class="searchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
        </span>
      </div>
      <el-collapse-transition>
        <el-form v-show="searchExpanded" :inline="true" :model="queryForm" class="search-form" @submit.native.prevent>
          <el-form-item label="关键字">
            <el-input v-model="queryForm.keyword" placeholder="姓名 / 科室 / openid" clearable style="width: 220px" />
          </el-form-item>
          <el-form-item label="角色">
            <el-select v-model="queryForm.role" placeholder="全部" clearable style="width: 140px">
              <el-option label="待审核" value="pending" />
              <el-option label="临床" value="clinical" />
              <el-option label="后勤" value="logistics" />
              <el-option label="管理员" value="admin" />
            </el-select>
          </el-form-item>
          <el-form-item class="search-buttons">
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh-right" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-collapse-transition>
    </el-card>

    <!-- 表格 -->
    <el-card shadow="never" class="table-card">
      <div class="table-toolbar">
        <span class="tip">小程序用户共 {{ total }} 人</span>
        <span>
          <el-button
            type="primary"
            icon="el-icon-user-solid"
            :disabled="!selectedRows.length"
            @click="openBatchRole"
          >批量设置角色{{ selectedRows.length ? `（${selectedRows.length}）` : '' }}</el-button>
          <el-button icon="el-icon-key" @click="openTokenInput">设置令牌</el-button>
          <el-button icon="el-icon-refresh" @click="fetchList">刷新</el-button>
        </span>
      </div>
      <el-table
        :data="list"
        border
        stripe
        ref="userTable"
        row-key="openid"
        class="mp-user-table"
        v-loading="loading"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="45" align="center" :reserve-selection="true" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="name" label="姓名" min-width="110" />
        <el-table-column prop="dept" label="科室" min-width="130" />
        <el-table-column prop="openid" label="微信标识(openid)" min-width="240" show-overflow-tooltip />
        <el-table-column label="已绑定" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.has_bound ? 'success' : 'info'" size="medium">
              {{ scope.row.has_bound ? '已绑定' : '未绑定' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="当前角色" width="110" align="center">
          <template slot-scope="scope">
            <el-tag :type="roleTagType(scope.row.role)" size="medium">{{ scope.row.role_display }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="170" align="center">
          <template slot-scope="scope">
            {{ formatTime(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center" fixed="right">
          <template slot-scope="scope">
            <!-- 待审核用户：可选角色不含 pending，避免把已开通人员误降回未授权 -->
            <el-select
              :value="scope.row.role"
              size="small"
              @change="changeRole(scope.row, $event)"
              style="width: 120px"
            >
              <el-option
                v-for="opt in assignableRoles"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="fetchList"
      />
    </el-card>

    <!-- 批量设置角色：上线培训时一次开通整个科室，避免几十人逐个下拉 -->
    <el-dialog title="批量设置角色" :visible.sync="batchDialogVisible" width="420px">
      <p class="batch-tip">
        已选 <b>{{ selectedRows.length }}</b> 人，将统一设为：
      </p>
      <el-select v-model="batchRole" placeholder="请选择角色" style="width: 100%">
        <el-option
          v-for="opt in assignableRoles"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        />
      </el-select>
      <div class="batch-names">
        {{ selectedNames }}
      </div>
      <span slot="footer">
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="batchSaving" @click="submitBatchRole">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api/mpUser'

export default {
  name: 'MpUserList',
  data() {
    return {
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      loading: false,
      searchExpanded: true,
      queryForm: { keyword: '', role: '' },
      tokenInput: '',
      showToken: false,
      // 批量设置角色（2026-10-27）：上线培训时一次开通整个科室
      selectedRows: [],
      batchDialogVisible: false,
      batchRole: '',
      batchSaving: false,
    }
  },
  computed: {
    // 可分配角色：不含 pending，防止把已开通人员误降回「未授权」状态
    assignableRoles() {
      return [
        { value: 'clinical', label: '临床' },
        { value: 'logistics', label: '后勤' },
        { value: 'admin', label: '管理员' },
      ]
    },
    // 弹窗里回显勾选了谁，避免选错人
    selectedNames() {
      const names = this.selectedRows.map((r) => r.name || r.openid)
      if (!names.length) return ''
      const shown = names.slice(0, 6).join('、')
      return names.length > 6 ? `${shown} 等 ${names.length} 人` : shown
    },
  },
  created() {
    this.showToken = !localStorage.getItem('ems_mp_admin_token')
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    roleLabel(v) {
      return {
        pending: '待审核',
        clinical: '临床',
        logistics: '后勤',
        admin: '管理员',
      }[v] || v
    },
    roleTagType(v) {
      return {
        pending: 'warning',
        clinical: 'info',
        logistics: 'warning',
        admin: 'success',
      }[v] || 'info'
    },
    formatTime(v) {
      if (!v) return ''
      // DRF 默认序列化：2026-09-17T12:34:56.789000Z -> 2026-09-17 12:34:56（去微秒与 Z，完整落在单格）
      const s = String(v).replace('T', ' ').split('.')[0]
      return s.endsWith('Z') ? s.slice(0, -1) : s
    },
    openTokenInput() {
      this.showToken = true
    },
    saveToken() {
      const t = (this.tokenInput || '').trim()
      if (!t) {
        this.$message.warning('请先粘贴令牌')
        return
      }
      localStorage.setItem('ems_mp_admin_token', t)
      window.location.reload()
    },
    buildParams() {
      const p = { page: this.currentPage }
      if (this.queryForm.keyword) p.keyword = this.queryForm.keyword
      if (this.queryForm.role) p.role = this.queryForm.role
      return p
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await api.list(this.buildParams())
        const data = res.data
        this.list = data.results || data
        this.total = data.count || (Array.isArray(data) ? data.length : 0)
      } catch (e) {
        // 错误已由拦截器提示
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    handleReset() {
      this.queryForm = { keyword: '', role: '' }
      this.currentPage = 1
      this.fetchList()
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    openBatchRole() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先勾选要设置的用户')
        return
      }
      this.batchRole = ''
      this.batchDialogVisible = true
    },
    async submitBatchRole() {
      if (!this.batchRole) {
        this.$message.warning('请选择要设置的角色')
        return
      }
      const openids = this.selectedRows.map((r) => r.openid)
      try {
        await this.$confirm(
          `确认把选中的 ${openids.length} 人统一设为「${this.roleLabel(this.batchRole)}」？`,
          '批量设置角色',
          { type: 'warning' }
        )
      } catch (e) {
        return
      }
      this.batchSaving = true
      try {
        const res = await api.batchSetRole(openids, this.batchRole)
        const data = res.data || {}
        this.$message.success(`已更新 ${data.updated || 0} 人`)
        if (data.not_found && data.not_found.length) {
          this.$message.warning(`${data.not_found.length} 个用户已不存在，已跳过`)
        }
        this.batchDialogVisible = false
        // 清掉表格内部勾选状态，避免刷新后 residual 选中
        this.selectedRows = []
        this.$nextTick(() => {
          if (this.$refs.userTable) this.$refs.userTable.clearSelection()
        })
        this.fetchList()
      } catch (e) {
        const d = e.response && e.response.data
        let msg = '批量设置失败，请检查后端服务'
        if (d && d.detail) msg = d.detail
        else if (d && d.role) msg = d.role[0]
        this.$message.error(msg)
      } finally {
        this.batchSaving = false
      }
    },
    async changeRole(row, newRole) {
      if (newRole === row.role) return
      try {
        await this.$confirm(
          `确认将「${row.name || row.openid}」的角色改为「${this.roleLabel(newRole)}」？`,
          '修改角色',
          { type: 'warning' }
        )
      } catch (e) {
        // 取消：重新拉取列表以还原下拉显示
        this.fetchList()
        return
      }
      try {
        await api.setRole(row.openid, newRole)
        this.$message.success('角色已更新')
        row.role = newRole
        row.role_display = this.roleLabel(newRole)
      } catch (e) {
        const d = e.response && e.response.data
        let msg = '修改失败，请检查后端服务'
        if (d && d.detail) msg = d.detail
        else if (d && d.role) msg = d.role[0]
        this.$message.error(msg)
        this.fetchList()
      }
    },
  },
}
</script>

<style scoped>
/* 批量设置角色弹窗 */
.batch-tip {
  margin: 0 0 10px;
  font-size: 13px;
  color: #606266;
}
.batch-tip b {
  color: #f56c6c;
  font-size: 15px;
}
.batch-names {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.7;
  color: #909399;
  word-break: break-all;
}
/* 内容区限宽居中：宽屏下不再硬撑满整行，避免弹性列被灌出大片空白 */
.mp-user-list {
  max-width: 1360px;
  margin: 0 auto;
  padding: 16px;
}
.search-card /deep/ .el-card__body {
  padding: 0;
}
.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.search-title {
  cursor: pointer;
  font-weight: 600;
  user-select: none;
}
.search-title:hover {
  color: #409eff;
}
.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 16px;
}
.search-form .el-form-item {
  margin-bottom: 12px;
}
.search-buttons {
  margin-left: auto;
}
.table-card {
  margin-top: 12px;
}
.table-toolbar {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.tip {
  color: #1e4266;
  font-weight: 600;
  font-size: 14px;
}
/* 表头：系统主题深蓝底白字加粗，与侧边栏配色呼应 */
.mp-user-table /deep/ thead th {
  background-color: #1e4266 !important;
  color: #ffffff !important;
  font-weight: 600;
}
.mp-user-table /deep/ .el-table__body tr:hover > td {
  background-color: #ecf5ff !important;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.token-alert {
  margin-bottom: 12px;
}
.token-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
</style>
